import { createSlice } from "@reduxjs/toolkit";
import {
  getWorkers,
  saveWorker,
  removeWorker,
  getLocalConcepts,
  saveLocalConcept,
  removeLocalConcept,
  getExpenseRecords,
  saveExpenseRecord,
  removeExpenseRecord
} from "./thunks";

const getInitialPeriod = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const initialState = {
  workers: [],
  localConcepts: [],
  records: [],
  selectedPeriod: getInitialPeriod(),
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  workersStatus: "idle",
  conceptsStatus: "idle",
  saveStatus: "idle",
  error: null,
};

export const expensesSlice = createSlice({
  name: "expenses",
  initialState,
  reducers: {
    setSelectedPeriod: (state, action) => {
      state.selectedPeriod = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Workers
    builder.addCase(getWorkers.pending, (state) => {
      state.workersStatus = "loading";
    });
    builder.addCase(getWorkers.fulfilled, (state, action) => {
      state.workers = action.payload || [];
      state.workersStatus = "succeeded";
    });
    builder.addCase(getWorkers.rejected, (state, action) => {
      state.workersStatus = "failed";
      state.error = action.error.message;
    });
    builder.addCase(saveWorker.fulfilled, (state, action) => {
      const idx = state.workers.findIndex((w) => w.id === action.payload.id);
      if (idx >= 0) {
        state.workers[idx] = action.payload;
      } else {
        state.workers.push(action.payload);
      }
    });
    builder.addCase(removeWorker.fulfilled, (state, action) => {
      state.workers = state.workers.filter((w) => w.id !== action.payload);
    });

    // Local Concepts
    builder.addCase(getLocalConcepts.pending, (state) => {
      state.conceptsStatus = "loading";
    });
    builder.addCase(getLocalConcepts.fulfilled, (state, action) => {
      state.localConcepts = action.payload || [];
      state.conceptsStatus = "succeeded";
    });
    builder.addCase(getLocalConcepts.rejected, (state, action) => {
      state.conceptsStatus = "failed";
      state.error = action.error.message;
    });
    builder.addCase(saveLocalConcept.fulfilled, (state, action) => {
      if (action.payload && action.payload.id) {
        const exists = state.localConcepts.some((c) => c.id === action.payload.id);
        if (!exists) state.localConcepts.push(action.payload);
      }
    });
    builder.addCase(removeLocalConcept.fulfilled, (state, action) => {
      state.localConcepts = state.localConcepts.filter((c) => c.id !== action.payload);
    });

    // Expense Records
    builder.addCase(getExpenseRecords.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(getExpenseRecords.fulfilled, (state, action) => {
      state.records = action.payload || [];
      state.status = "succeeded";
    });
    builder.addCase(getExpenseRecords.rejected, (state, action) => {
      state.status = "failed";
      state.error = action.error.message;
    });
    builder.addCase(saveExpenseRecord.pending, (state) => {
      state.saveStatus = "loading";
    });
    builder.addCase(saveExpenseRecord.fulfilled, (state, action) => {
      state.saveStatus = "succeeded";
      const idx = state.records.findIndex((r) => r.id === action.payload.id);
      if (idx >= 0) {
        state.records[idx] = action.payload;
      } else {
        state.records.push(action.payload);
      }
    });
    builder.addCase(saveExpenseRecord.rejected, (state, action) => {
      state.saveStatus = "failed";
      state.error = action.error.message;
    });
    builder.addCase(removeExpenseRecord.fulfilled, (state, action) => {
      state.records = state.records.filter((r) => r.id !== action.payload);
    });
  },
});

export const { setSelectedPeriod } = expensesSlice.actions;

export default expensesSlice.reducer;
